import { useState, useEffect, useCallback, useMemo } from 'react';

const STORAGE_KEY = 'rbx-theme';

function getSystemPreference() {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function getThemePreference() {
  try {
    return localStorage.getItem(STORAGE_KEY) || 'system';
  } catch {
    return 'system';
  }
}

export function resolveTheme(preference) {
  return preference === 'system' ? getSystemPreference() : preference;
}

export function applyTheme(resolved) {
  document.documentElement.setAttribute('data-theme', resolved);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.content = resolved === 'dark' ? '#0f0f1a' : '#f4f5f8';
  }
  document.querySelectorAll('.cmt-widget').forEach(el => {
    el.classList.toggle('cmt-dark', resolved === 'dark');
  });
}

export function useTheme() {
  const [preference, setPreference] = useState(getThemePreference);

  const resolved = useMemo(() => resolveTheme(preference), [preference]);

  useEffect(() => {
    applyTheme(resolved);
    try { localStorage.setItem(STORAGE_KEY, preference); } catch { /* noop */ }
  }, [resolved, preference]);

  useEffect(() => {
    if (preference !== 'system') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme(getSystemPreference());
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [preference]);

  const toggleTheme = useCallback(() => {
    setPreference(prev => prev === 'light' ? 'dark' : prev === 'dark' ? 'system' : 'light');
  }, []);

  return { theme: resolved, themePreference: preference, toggleTheme };
}
